import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { clinicInfo } from '../../mock';

const VIDEO_START = 1.75;

export default function Hero({ onBookClick }) {
  const videoRef = React.useRef(null);
  const [videoSrc, setVideoSrc] = React.useState(null);
  const [videoReady, setVideoReady] = React.useState(false);

  React.useEffect(() => {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!isDesktop || saveData || reduceMotion) return;
    const start = () => {
      const px = window.innerWidth * Math.min(window.devicePixelRatio || 1, 2);
      setVideoSrc(px > 2200 ? 'hero-native' : 'hero-1080');
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, []);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;
    v.muted = true;
    const onMeta = () => {
      try { if (v.currentTime < VIDEO_START) v.currentTime = VIDEO_START; } catch (e) { /* ignore */ }
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    const onPlaying = () => setVideoReady(true);
    v.addEventListener('loadedmetadata', onMeta, { once: true });
    v.addEventListener('playing', onPlaying, { once: true });
    v.load();
    return () => {
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('playing', onPlaying);
    };
  }, [videoSrc]);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[#0B0B0B]">
      <div className="absolute inset-0 bg-[#0B0B0B]">
        <picture>
          <source
            media="(max-width: 767px)"
            srcSet="/img/hero-poster-mobile-600.webp 600w, /img/hero-poster-mobile-900.webp 900w"
            sizes="100vw"
            type="image/webp"
          />
          <source
            srcSet="/img/hero-poster-1280.webp 1280w, /img/hero-poster-1920.webp 1920w, /img/hero-poster-2560.webp 2560w"
            sizes="100vw"
            type="image/webp"
          />
          <img
            src="/img/hero-poster-1920.webp"
            alt="Beechwood Dental Studio at 18 Fischer-Hallman Road North, Waterloo"
            width={1920}
            height={1080}
            fetchpriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        {videoSrc && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            loop
            muted
            playsInline
            preload="metadata"
            poster="/img/hero-poster-1920.webp"
            aria-hidden="true"
          >
            <source src={`/video/${videoSrc}.webm`} type="video/webm" />
            <source src={`/video/${videoSrc}.mp4`} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/60 via-[#0B0B0B]/35 to-[#0B0B0B]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/70 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 min-h-screen max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-center pt-32 pb-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 text-[#EFE9DF]/80 mb-8">
            <span className="h-px w-10 bg-[#C8A97E]" />
            <span className="text-[10px] tracking-[0.32em] uppercase">{clinicInfo.city}</span>
          </div>
          <h1 className="font-display text-[#FAF7F2] text-[52px] md:text-[84px] lg:text-[104px] leading-[0.95] tracking-[-0.02em]">
            Your trusted<br />
            <span className="italic text-[#C8A97E]">family dentist</span><br />
            in Waterloo.
          </h1>
          <p className="mt-8 text-[#EFE9DF]/85 text-lg md:text-xl max-w-xl leading-relaxed">
            Compassionate, modern dental care for every smile in your family — delivered by a team that listens first.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Button
              onClick={onBookClick}
              className="btn-primary bg-[#C8A97E] hover:bg-[#B69463] text-[#0B0B0B] rounded-none h-14 px-8 text-[11px] tracking-[0.28em] uppercase font-medium"
            >
              Book Online
              <ArrowUpRight size={16} className="ml-2" strokeWidth={1.5} />
            </Button>
            <Link to="/services" className="group flex items-center gap-3 text-[#FAF7F2] text-[11px] tracking-[0.28em] uppercase">
              <span className="nav-link">Explore Services</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
